import Vue from 'vue';
import { mapMutations, mapState } from 'vuex';
import Errors from '../classes/Errors';
import Autocomplete from '../classes/Autocomplete';

import gms from '../store/gms';

Vue.component('booker-information-component', {
    props: {
        countries: {
            type: Array,
            default: () => [],
        },
        languages: {
            type: Array,
            default: () => [],
        },
        locale: {
            type: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            gms,
            players: [],
            autocomplete: new Autocomplete(this),
        };
    },

    computed: {
        filteredCountries() {
            const defaultCountry = _.find(this.countries, { code: 'NL' });
            const disabled = {
                disabled: true,
            };

            return [defaultCountry, disabled, ...this.countries];
        },

        register: {
            get() {
                return this.$store.state.reservation.booker.register;
            },
            set(value) {
                this.setBooker(this.booker);
                this.$store.commit('reservation/updateRegister', value);
            },
        },

        newsletterOptIn: {
            get() {
                return this.$store.state.reservation.booker.newsletterOptIn;
            },
            set(value) {
                this.setBooker(this.booker);
                this.$store.commit('reservation/updateNewsletterOptIn', value);
            },
        },

        ...mapState('reservation', {
            section: (state) => state.section,
            participants: (state) => state.participants,
            booker: (state) => _.cloneDeep(state.booker),
            month: (state) => state.month,
            date: (state) => state.date,
            activeRound: (state) => state.activeRound,
        }),

        ...mapState('member', {
            memberProfile: (state) => state.profile,
        }),
    },

    watch: {
        participants(value) {
            this.players = [];
            for (let i = 0; i < value - 1; i++) {
                this.players[i] = {
                    uuid: null,
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    phoneCountryCode: 'NL',
                    member: false,
                    bookingConfirmation: true,
                };
            }
        },
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        ...mapMutations('reservation', [
            'setSection',
            'setBooker',
            'setBookerAttribute',
        ]),

        /**
         * Applies the set booking model to the
         * rest of the instance variables.
         *
         * @returns {Void}
         */
        setPosition(mainBooker) {
            this.booker.firstName = mainBooker.firstName;
            this.booker.lastName = mainBooker.lastName;
            this.booker.flightId = mainBooker.flightId;
            this.booker.memberIndex = mainBooker.index;

            this.setBooker(this.booker);
        },

        /**
         * Returns booker data for GMS service calls.
         *
         * @returns {Object}
         */
        getData() {
            const data = {
                firstName: this.booker.firstName,
                lastName: this.booker.lastName,
                email: this.booker.email,
                phoneCountryCode: this.findPhoneCountryCode(),
                phoneNumber: this.booker.phoneNumber,
                flightId: this.booker.flightId || this.gms.flights[0].id,
                memberIndex: this.booker.memberIndex || 0,
                register: this.booker.register,
                newsletterOptIn: this.booker.newsletterOptIn,
                termsAndConditionsAccepted: this.booker.termsAndConditionsAccepted,
                password: this.booker.password,
                password_confirm: this.booker.password_confirm,
                uuid: (this.booker.uuid) ? this.booker.uuid : null,
                languageUuid: this.getCurrentLanguage().id,
                participants: this.participants,
            };

            for (let i = 0; i < this.players.length; i++) {
                data[`player-${i + 1}-uuid`] = this.players[i].uuid;
                data[`player-${i + 1}-firstname`] = this.players[i].firstName;
                data[`player-${i + 1}-lastname`] = this.players[i].lastName;
                data[`player-${i + 1}-email`] = this.players[i].email;
                data[`player-${i + 1}-phoneCountryCode`] = this.players[i].phoneCountryCode;
                data[`player-${i + 1}-phone`] = this.players[i].phone;
                data[`player-${i + 1}-member`] = this.players[i].member;
                data[`player-${i + 1}-bookingConfirmation`] = this.players[i].bookingConfirmation;
            }

            return data;
        },

        /**
         * Find country code (eg NL) for booker.
         *
         * @returns {String}
         */
        findPhoneCountryCode() {
            // Check if country is set on booker.
            if (this.booker.phoneCountry) {
                // Return country code if it's set.
                if (this.booker.phoneCountry.code) {
                    return this.booker.phoneCountry.code;
                }

                // Try to find the code by the country code.
                const country = _.find(this.countries, { countryCode: this.booker.phoneCountry.countryCode });
                if (country && country.code) {
                    return country.code;
                }
            }

            // Default to NL if everything fails.
            return 'NL';
        },

        setCountryCode(payload) {
            this.booker.phoneCountry = payload;
            this.setBooker(this.booker);
        },

        setPlayerCountryCode(player, countryCode) {
            player.phoneCountryCode = countryCode;
        },

        /**
         * Returns the booking component language.
         *
         * @returns {Object} - GMS Language Object
         */
        getCurrentLanguage() {
            if (this.locale === 'nl') {
                return _.find(this.languages, { code: 'nl' });
            }

            return _.find(this.languages, { code: 'en' });
        },

        onChangeBookingClick() {
            this.$modals.$emit('show-modal:reset-booking', this.reset.bind(this));
        },

        /**
         * Resets the booking component.
         *
         * @returns {Void}
         */
        reset() {
            window.onbeforeunload = null;
            window.location.reload();
        },

        /**
         * Validates the booker in the reservation flow.
         *
         * @returns {Object|Void}
         */
        validate() {
            if (this.submitting) {
                return;
            }
            this.submitting = true;

            if (!this.memberProfile) {
                this.setBooker(this.booker);
            }

            // If succesfull, we go the personal info.
            this.$api.gms.reservation.setMainBooker(this.gms.visitId, this.getData())
            .then((response) => {
                if (response.accountAlreadyExists) {
                    this.booker.register = false;
                }

                if (this.booker.register) {
                    this.booker.uuid = response.uuid;
                }

                // Loop flights to update claims with other players.
                let playerIndex = 0;
                let updatedClaims = 0;
                for (let i = 0; i < this.gms.flights.length; i++) {
                    // Fetch flight and update players.
                    const flight = this.gms.flights[i];
                    for (let j = 0; j < flight.players.length; j++) {
                        if (i === 0 && j === 0) {
                            flight.players[j].uuid = this.memberProfile ? this.booker.uuid : null;
                            flight.players[j].firstName = this.booker.firstName;
                            flight.players[j].lastName = this.booker.lastName;
                            flight.players[j].email = this.booker.email;
                            flight.players[j].phoneCountryCode = this.findPhoneCountryCode();
                            flight.players[j].phoneNumber = this.booker.phoneNumber;
                            flight.players[j].member = !!this.memberProfile;
                            flight.players[j].mainBooker = true;
                            flight.players[j].defaultNameState = false;
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                        flight.players[j].uuid = this.players[playerIndex].uuid;
                        flight.players[j].firstName = this.players[playerIndex].firstName ? this.players[playerIndex].firstName : flight.players[j].defaults.firstName;
                        flight.players[j].lastName = this.players[playerIndex].lastName ? this.players[playerIndex].lastName : flight.players[j].defaults.lastName;
                        flight.players[j].email = this.players[playerIndex].email;
                        flight.players[j].phoneCountryCode = this.players[playerIndex].phoneCountryCode;
                        flight.players[j].phoneNumber = this.players[playerIndex].phone;
                        flight.players[j].member = this.players[playerIndex].member;
                        flight.players[j].bookingConfirmation = this.players[playerIndex].bookingConfirmation;
                        flight.players[j].defaultNameState = false;
                        // eslint-disable-next-line no-plusplus
                        playerIndex++;
                    }

                    // Create post object for claim update.
                    const obj = {
                        date: this.date,
                        nrOfHoles: flight.claims[0].numberOfHoles,
                        startHole: flight.claims[0].startHole,
                        startTime: flight.claims[0].startTime,
                        members: flight.players,
                    };

                    // Update claim.
                    this.$api.gms.reservation.updateClaim(flight.claims[0].reservationId, postQs(obj))
                    // eslint-disable-next-line no-loop-func
                    .then((data) => {
                        // Go to next screen if all claims have been updated.
                        // eslint-disable-next-line no-plusplus
                        updatedClaims++;
                        if (updatedClaims === this.gms.flights.length) {
                            this.booker.confirmed = true;
                            this.setBooker(this.booker);
                            this.setSection('overview');
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        alert('Error updating players names.');
                    });
                }
            })
            .catch((error) => {
                console.log(error);
                this.errors.record(error.errors);
                this.submitting = false;
                this.$nextTick(this.errors.scrollToFirst);
            });
        },

        /**
         * Validates the first and last name of the booker.
         *
         * @returns {Void}
         */
        validateNames() {
            this.booking.$api.gms.reservation.setMainBookerNames({
                firstName: this.firstName,
                lastName: this.lastName,
            }).then((response) => {
                this.namesConfirmed = true;
                this.booking.flights = this.booking.arrangement.fillFlights(this.booking.numberOfPlayers);
                window.raiseGtmPageview('Step 4 - Arrange Groups');
            }).catch((error) => {
                this.errors.record(error.errors);
            });
        },

        /**
         * Register the daymember with the GMS.
         *
         * @returns {Void}
         */
        createAccount() {
            this.booking.$api.gms.account.createDaymemberAccount({
                uuid: this.uuid,
                password: this.password,
            })
            .then((response) => {
                console.log(response);
            })
            .catch((error) => {
                console.log(error);
            });
        },
    },
});
